import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, OverlayTrigger, Popover, Row, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ng } from "@common/ngds";
import { getClientId } from "@common/helpers/funlib";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import type { NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { AlertMessage } from "../ui-components/SXAlert/SXAlertMessage";
import { useAppSelector } from "../../app/hooks";
import { useProject } from "../../features/projects/projectSlice";
import { DEFAULT_FIRE_DURATION_MINUTES, hasFireCombination } from "./shared";

const DEFAULT_STEEL_FIRE_TEMPERATURE = 400;
const DEFAULT_STEEL_FIRE_PROTECTION_THICKNESS_METERS = 10e-3;

const STEEL_ENCASEMENT_LABELS: Record<ng.fire_protection.steel.Encasement, string> = {
  [ng.fire_protection.steel.Encasement.ENCASEMENT_UNSPECIFIED]: "",
  [ng.fire_protection.steel.Encasement.ENCASEMENT_CONTOUR]: "contour_.label",
  [ng.fire_protection.steel.Encasement.ENCASEMENT_HOLLOW]: "hollow_.label",
};

type SteelFireMaterialListEntry = {
  guid: string;
  name: string;
};

export function SXInputSteelFireSettings() {
  const { t } = useTranslation();
  const token = useAppSelector((state) => state.UserSlice.token);
  const {
    project: selProject,
    setSteelFireDuration,
    setSteelFireTimeStep,
    setSteelFireExposure,
    setSteelFireTemperatureCurve,
    setSteelFireCritDeflection,
    setSteelFireProtectionMode,
    setSteelFireProtectingMaterial,
    setSteelFireProtectionThickness,
    setSteelFireProtectionTemperature,
  } = useProject();

  const fireSettingsDisabled = !hasFireCombination(selProject);

  const nationalAnnex = selProject?.data?.data?.input?.ec?.nationalAnnex;
  const annex =
    nationalAnnex != null ? ng.eurocode.Annex_FromObject(nationalAnnex) : ng.eurocode.Annex.ANNEX_UNSPECIFIED;
  const isDenmarkAnnex = annex === ng.eurocode.Annex.ANNEX_DENMARK;

  const element = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0] as
    | { designSettings?: ng.design.ElementDesignSettings_json }
    | undefined;
  const steelFire = element?.designSettings?.steel?.fire;
  const generalFire = element?.designSettings?.general?.generalFire;

  const duration =
    typeof generalFire?.durationOfFire === "number" ? generalFire.durationOfFire : DEFAULT_FIRE_DURATION_MINUTES;
  const timeStep = typeof generalFire?.timeStep === "number" ? generalFire.timeStep : 10;
  const sectionExposure =
    steelFire?.sectionExposure != null
      ? ng.design.steel.SectionExposure_FromObject(steelFire.sectionExposure)
      : ng.design.steel.SectionExposure.SECTION_EXPOSURE_ALL_SIDES;
  const temperatureCurve =
    generalFire?.temperatureCurve != null
      ? ng.design.general.TemperatureCurve_FromObject(generalFire.temperatureCurve)
      : ng.design.general.TemperatureCurve.TEMPERATURE_CURVE_STANDARD;
  const critDeflection = steelFire?.critDeflection ?? false;

  const timeStepItems = useMemo<NGDropdownListItem<number>[]>(
    () => [10, 20, 30].map((v) => ({ id: v, name: v.toString() })),
    [],
  );
  const exposureItems = useMemo<NGDropdownListItem<ng.design.steel.SectionExposure>[]>(
    () => [
      { id: ng.design.steel.SectionExposure.SECTION_EXPOSURE_ALL_SIDES, name: t("all_sides_.label") },
      { id: ng.design.steel.SectionExposure.SECTION_EXPOSURE_THREE_SIDES, name: t("three_sides_.label") },
      { id: ng.design.steel.SectionExposure.SECTION_EXPOSURE_FLANGE_ONLY, name: t("only_flange_.label") },
    ],
    [t],
  );
  const temperatureCurveItems = useMemo<NGDropdownListItem<ng.design.general.TemperatureCurve>[]>(
    () => [
      { id: ng.design.general.TemperatureCurve.TEMPERATURE_CURVE_STANDARD, name: t("standard_.label") },
      { id: ng.design.general.TemperatureCurve.TEMPERATURE_CURVE_EXTERNAL, name: t("external_.label") },
      { id: ng.design.general.TemperatureCurve.TEMPERATURE_CURVE_HYDROCARBON, name: t("hydro_carbon_.label") },
    ],
    [t],
  );

  const selectedTimeStep = timeStepItems.find((item) => item.id === timeStep) ?? null;
  const selectedExposure = exposureItems.find((item) => item.id === sectionExposure) ?? null;
  const selectedTemperatureCurve = temperatureCurveItems.find((item) => item.id === temperatureCurve) ?? null;

  const showInvalidDurationAlert = () => {
    AlertMessage("warning", t("warning_.label"), "", t("fire_warning_duration_not_valid_.label"));
  };

  // steelFire is Fire_json — the oneof protection_type is serialised as either
  // `protectingMaterial` or `temperature` directly in the JSON (not as `protection_type`).
  const isMaterialProtection = steelFire?.protectingMaterial != null;
  const isTemperatureProtection = steelFire?.temperature != null;
  const activeSteelProtectionTab = isMaterialProtection ? "mtrl" : "temp";
  const [activeSteelProtectionTabUi, setActiveSteelProtectionTabUi] = useState<"mtrl" | "temp">(
    activeSteelProtectionTab,
  );
  const isSteelProtectionModeUpdating = activeSteelProtectionTabUi !== activeSteelProtectionTab;

  const steelProtectionGuid = isMaterialProtection ? (steelFire?.protectingMaterial?.guid ?? "none_") : "none_";
  const steelProtectionThicknessMm =
    Number(
      isMaterialProtection
        ? (steelFire?.protectingMaterial?.thickness ?? DEFAULT_STEEL_FIRE_PROTECTION_THICKNESS_METERS)
        : DEFAULT_STEEL_FIRE_PROTECTION_THICKNESS_METERS,
    ) * 1000;
  const steelProtectionTemperature = Number(
    isTemperatureProtection
      ? (steelFire?.temperature ?? DEFAULT_STEEL_FIRE_TEMPERATURE)
      : DEFAULT_STEEL_FIRE_TEMPERATURE,
  );

  const inputMaterialDb = useMemo(() => {
    return ((selProject?.data?.data?.input?.mtrlDb ?? []) as Array<ng.material.Data_json | ng.material.IData>)
      .map((mtrl) => ng.material.Data.fromObject(mtrl as any))
      .filter((mtrl) => Boolean(mtrl?.id?.guid) && mtrl.getFireSteelData() !== null);
  }, [selProject]);

  const steelMaterialItems = useMemo<NGDropdownListItem<string>[]>(() => {
    const dbItems = inputMaterialDb.map((mtrl) => ({
      id: mtrl.id?.guid ?? "",
      name: mtrl.id?.name ?? mtrl.id?.guid ?? t("select_.label"),
    }));

    const seen = new Set<string>();
    const unique = dbItems.filter((item) => {
      if (!item.id || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });

    return [{ id: "none_", name: t("none_.label") }, ...unique];
  }, [inputMaterialDb, t]);

  const [steelMaterialDataByGuid, setSteelMaterialDataByGuid] = useState<Record<string, ng.material.Data>>({});
  const [steelFirePropsByGuid, setSteelFirePropsByGuid] = useState<
    Record<
      string,
      {
        density?: number | string;
        specificHeat?: number | string;
        thermalConductivity?: number | string;
        encasement?: ng.fire_protection.steel.Encasement | string | null;
      }
    >
  >({});

  const selectedSteelMaterialData =
    steelMaterialDataByGuid[steelProtectionGuid] ??
    inputMaterialDb.find((mtrl) => mtrl.id?.guid === steelProtectionGuid) ??
    null;
  const selectedSteelMaterialProps =
    steelFirePropsByGuid[steelProtectionGuid] ?? selectedSteelMaterialData?.getFireSteelData?.()?.properties;
  const steelDensity = Number(selectedSteelMaterialProps?.density ?? 0);
  const steelSpecificHeat = Number(selectedSteelMaterialProps?.specificHeat ?? 0);
  const steelThermalConductivity = Number(selectedSteelMaterialProps?.thermalConductivity ?? 0);
  const steelEncasementRaw =
    selectedSteelMaterialProps?.encasement ?? ng.fire_protection.steel.Encasement.ENCASEMENT_UNSPECIFIED;
  const steelEncasement = ng.fire_protection.steel.Encasement_FromObject(steelEncasementRaw);
  const steelEncasementLabelKey = STEEL_ENCASEMENT_LABELS[steelEncasement] ?? "";
  const steelEncasementLabel = steelEncasementLabelKey ? t(steelEncasementLabelKey) : "";
  const [backendSteelMaterialItems, setBackendSteelMaterialItems] = useState<SteelFireMaterialListEntry[]>([]);

  useEffect(() => {
    setActiveSteelProtectionTabUi(activeSteelProtectionTab);
  }, [activeSteelProtectionTab]);

  useEffect(() => {
    const map: Record<string, ng.material.Data> = {};
    for (const mtrl of inputMaterialDb) {
      const guid = mtrl.id?.guid;
      if (!guid) continue;
      map[guid] = mtrl;
    }
    setSteelMaterialDataByGuid((prev) => ({ ...map, ...prev }));
  }, [inputMaterialDb]);

  useEffect(() => {
    let disposed = false;

    const loadSteelFireProtectionMaterials = async () => {
      try {
        const requestHeaders: HeadersInit = {
          authorization: token ? `Bearer ${token}` : "",
          "x-client-id": getClientId(),
        };

        const annexQuery = encodeURIComponent(String(annex));
        let page = 1;
        let totalPages = 1;
        const collected = new Map<string, SteelFireMaterialListEntry>();

        while (page <= totalPages) {
          const resp = await fetch(`/api/materials/list?page=${page}&annex=${annexQuery}`, {
            method: "GET",
            headers: requestHeaders,
          });

          if (!resp.ok) {
            throw new Error(`Failed to fetch materials list page ${page}`);
          }

          const json = (await resp.json()) as {
            kind?: string;
            data?: Record<string, Array<{ guid?: string; id?: string; name?: string }>>;
            pagination?: { TotalPageCount?: number };
            error?: unknown;
          };

          if (json.kind === "error") {
            throw new Error(typeof json.error === "string" ? json.error : "Materials list returned error");
          }

          const grouped = json.data ?? {};
          const fireSteelKey =
            Object.keys(grouped).find((key) => key.trim().toLowerCase() === "fire protection steel") ??
            Object.keys(grouped).find((key) => {
              const k = key.toLowerCase();
              return k.includes("fire") && k.includes("steel");
            });

          if (fireSteelKey) {
            for (const entry of grouped[fireSteelKey] ?? []) {
              const guid = entry.guid ?? entry.id ?? "";
              if (!guid || collected.has(guid)) continue;
              collected.set(guid, { guid, name: entry.name ?? guid });
            }
          }

          const nextTotal = Number(json.pagination?.TotalPageCount ?? 1);
          totalPages = Number.isFinite(nextTotal) && nextTotal > 0 ? nextTotal : 1;
          page += 1;
        }

        if (!disposed) {
          setBackendSteelMaterialItems(Array.from(collected.values()));
        }
      } catch {
        // Best effort — keep local material list if backend fetch fails.
      }
    };

    void loadSteelFireProtectionMaterials();

    return () => {
      disposed = true;
    };
  }, [token, annex]);

  useEffect(() => {
    const guid = steelProtectionGuid;
    if (guid === "none_" || !guid) {
      return;
    }

    if (steelMaterialDataByGuid[guid] && steelFirePropsByGuid[guid]) {
      return;
    }

    const loadSteelMaterialDetail = async () => {
      try {
        const requestHeaders: HeadersInit = {
          authorization: token ? `Bearer ${token}` : "",
          "x-client-id": getClientId(),
        };

        const resp = await fetch(`/api/materials/${encodeURIComponent(guid)}`, {
          method: "GET",
          headers: requestHeaders,
        });

        if (!resp.ok) {
          return;
        }

        const json = (await resp.json()) as {
          kind?: string;
          data?: {
            dbMtrl?: ng.material.Data_json;
            data?: {
              density?: number | string;
              specificHeat?: number | string;
              thermalConductivity?: number | string;
              encasement?: ng.fire_protection.steel.Encasement | string | null;
            };
          };
        };

        const dbMtrl = json?.data?.dbMtrl;
        if (dbMtrl) {
          const materialData = ng.material.Data.fromObject(dbMtrl as any);
          setSteelMaterialDataByGuid((prev) => ({ ...prev, [guid]: materialData }));
        }

        const fireProps = json?.data?.data;
        if (fireProps) {
          setSteelFirePropsByGuid((prev) => ({ ...prev, [guid]: fireProps }));
        }
      } catch {
        // Best effort hydrate selected material values.
      }
    };

    void loadSteelMaterialDetail();
  }, [steelProtectionGuid, steelMaterialDataByGuid, steelFirePropsByGuid, token]);

  const mergedSteelMaterialItems = useMemo<NGDropdownListItem<string>[]>(() => {
    const all = new Map<string, NGDropdownListItem<string>>();

    for (const item of steelMaterialItems) {
      if (!item.id || item.id === "none_") continue;
      all.set(item.id, item);
    }

    for (const item of backendSteelMaterialItems) {
      if (!item.guid) continue;
      all.set(item.guid, { id: item.guid, name: item.name });
    }

    return [{ id: "none_", name: t("none_.label") }, ...Array.from(all.values())];
  }, [steelMaterialItems, backendSteelMaterialItems, t]);

  const selectedDropdownMtrl = mergedSteelMaterialItems.find((item) => item.id === steelProtectionGuid) ?? null;

  return (
    <>
      <Container fluid id="sx-table-container" className="ps-0 px-0">
        <Container style={{ color: "#696969" }}>
          {fireSettingsDisabled && (
            <Row className="p-2 border rounded mb-2" style={{ borderColor: "var(--panel-border)" }}>
              <Col>
                <div className="mt-2" style={{ color: "#696969" }}>
                  <span style={{ fontWeight: "bold" }}>
                    {t("fire_load_combination_needs_to_be_added_.label")}
                  </span>
                </div>
              </Col>
            </Row>
          )}

          <Row className="container-row" style={{ alignItems: "start" }}>
            <Col xs={4} className="ps-0">
              <div className="editfield-label">
                <span>{t("duration_of_fire_.label")}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="ps-0 px-0" style={{ height: 32, flex: 1 }}>
                  <SXEditField
                    id="sx-steel-fire-duration"
                    type="number"
                    val={duration}
                    disabled={fireSettingsDisabled}
                    disableNegativeNumber
                    onUpdate={(value) => {
                      const nextValue = Number(value);
                      if (Number.isFinite(nextValue) && nextValue > 0 && nextValue <= 1000) {
                        setSteelFireDuration(nextValue);
                        return;
                      }
                      showInvalidDurationAlert();
                    }}
                  />
                </div>
                <var>{t("minutes_.label")}</var>
              </div>
            </Col>

            <Col xs={3}>
              <div className="editfield-label">
                <span>{t("time_step_.label")}</span>
              </div>
              <div className="ps-0 px-0" style={{ height: 32 }}>
                <SXDropdown
                  id="sx-steel-fire-timestep-dropdown"
                  toggleId="sx_steel_fire_timestep_toggle"
                  className="sx-dropdown"
                  dataTestId="dropdown_steel_fire_timestep"
                  name="steel_fire_timestep"
                  lang="en"
                  disabled={fireSettingsDisabled}
                  list={timeStepItems}
                  val={selectedTimeStep}
                  onUpdate={(value) => value && setSteelFireTimeStep(value.id)}
                  rawNames
                  localisation={false}
                />
              </div>
            </Col>

            <Col xs={5}>
              <div className="editfield-label">
                <span>{t("exposure_.label")}</span>
              </div>
              <div className="ps-0 px-0" style={{ height: 32 }}>
                <SXDropdown
                  id="sx-steel-fire-exposure-dropdown"
                  toggleId="sx_steel_fire_exposure_toggle"
                  className="sx-dropdown"
                  dataTestId="dropdown_steel_fire_exposure"
                  name="steel_fire_exposure"
                  lang="en"
                  disabled={fireSettingsDisabled}
                  list={exposureItems}
                  val={selectedExposure}
                  onUpdate={(value) => value && setSteelFireExposure(value.id)}
                  rawNames
                  localisation={false}
                />
              </div>
            </Col>
          </Row>

          <Row className="container-row" style={{ alignItems: "start" }}>
            <Col xs={6} className="ps-0">
              <div className="editfield-label">
                <span>{t("temperature_curve_.label")}</span>
              </div>
              <div className="ps-0 px-0" style={{ height: 32 }}>
                <SXDropdown
                  id="sx-steel-fire-tempcurve-dropdown"
                  toggleId="sx_steel_fire_tempcurve_toggle"
                  className="sx-dropdown"
                  dataTestId="dropdown_steel_fire_tempcurve"
                  name="steel_fire_tempcurve"
                  lang="en"
                  disabled={fireSettingsDisabled}
                  list={temperatureCurveItems}
                  val={selectedTemperatureCurve}
                  onUpdate={(value) => value && setSteelFireTemperatureCurve(value.id)}
                  rawNames
                  localisation={false}
                />
              </div>
            </Col>

            {isDenmarkAnnex && (
              <Col xs={6} className="editfield-label" style={{ alignSelf: "end", paddingBottom: 4 }}>
                <Form.Check
                  type="switch"
                  className="sx-switch"
                  id="sx-steel-fire-critdef"
                  label={t("critical_deflection_.label")}
                  checked={critDeflection}
                  disabled={fireSettingsDisabled}
                  onChange={(e) => setSteelFireCritDeflection(e.target.checked)}
                />
              </Col>
            )}
          </Row>
        </Container>

        <Container id="fire_protection_steel" style={{ color: "#696969" }} className="mt-2">
          <Row className="mb-1" style={{ alignItems: "center" }}>
            <Col>
              <span style={{ fontWeight: "bold" }}>{t("fire_protection_.label")}</span>
            </Col>
            <Col xs="auto">
              {isSteelProtectionModeUpdating && (
                <small
                  className="editfield-label"
                  aria-live="polite"
                  style={{ opacity: 0.8, transition: "opacity 120ms ease" }}
                >
                  {t("updating_.label", "Updating...")}
                </small>
              )}
            </Col>
          </Row>

          <Container className="sx-table-style-for-custom-table px-0">
            <Tabs
              id="sx-steel-fire-protection-tabs"
              activeKey={activeSteelProtectionTabUi}
              className="simplex-custom-tabs p-0"
              onSelect={(key) => {
                if (key === "mtrl" || key === "temp") {
                  setActiveSteelProtectionTabUi(key);
                  setSteelFireProtectionMode(key);
                }
              }}
            >
              <Tab
                eventKey="mtrl"
                title={<strong>{t("apply_fire_protection_material_.label")}</strong>}
                tabClassName="px-3"
                disabled={fireSettingsDisabled}
              >
                <Container id="ngtable" className="ps-2 pt-2">
                  <Row className="container-row">
                    <Col className="editfield-label" xs={12}>
                      {t("protecting_material_.label")}
                    </Col>
                    <Col xs={6} className="ps-0">
                      <SXDropdown
                        id="sx-steel-fire-mtrl-dropdown"
                        toggleId="sx_steel_fire_mtrl_toggle"
                        className="sx-dropdown"
                        dataTestId="dropdown_steel_fire_mtrl"
                        name="steel_fire_mtrl"
                        lang="en"
                        disabled={fireSettingsDisabled || activeSteelProtectionTabUi !== "mtrl"}
                        list={mergedSteelMaterialItems}
                        val={selectedDropdownMtrl}
                        onUpdate={(value) => value && setSteelFireProtectingMaterial(value.id)}
                        rawNames
                        localisation={false}
                      />
                    </Col>
                    <Col>
                      <OverlayTrigger
                        trigger="click"
                        rootClose
                        placement="right"
                        overlay={
                          <Popover id="sx-steel-fire-material-info-popover">
                            <Popover.Header as="h3">{t("material_.label")}</Popover.Header>
                            <Popover.Body>
                              <div className="mb-1">
                                <strong>{t("density_.label")}: </strong>
                                <span>{steelDensity}</span>
                              </div>
                              <div className="mb-1">
                                <strong>{t("specific_heat_.label")}: </strong>
                                <span>{steelSpecificHeat}</span>
                              </div>
                              <div className="mb-1">
                                <strong>{t("thermal_conductivity_.label")}: </strong>
                                <span>{steelThermalConductivity}</span>
                              </div>
                              <div>
                                <strong>{t("encasement_.label")}: </strong>
                                <span>{steelEncasementLabel || t("unspecified_.label")}</span>
                              </div>
                            </Popover.Body>
                          </Popover>
                        }
                      >
                        <Button
                          variant="link"
                          id="sx-steel-fire-material-info-btn"
                          aria-label={t("material_.label")}
                          disabled={fireSettingsDisabled || activeSteelProtectionTabUi !== "mtrl"}
                          style={{
                            marginLeft: 8,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: "1px solid var(--btn-border-color)",
                            color: "var(--text-gray)",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          i
                        </Button>
                      </OverlayTrigger>
                    </Col>
                  </Row>

                  <Row className="container-row">
                    <Col className="editfield-label">{t("thickness_.label")} (mm)</Col>
                    <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                      <SXEditField
                        id="sx-steel-fire-thickness"
                        fullWidth
                        textAlign="left"
                        type="number"
                        val={steelProtectionThicknessMm}
                        disabled={
                          fireSettingsDisabled ||
                          activeSteelProtectionTabUi !== "mtrl" ||
                          steelProtectionGuid === "none_"
                        }
                        disableNegativeNumber
                        onUpdate={(v) => setSteelFireProtectionThickness(Number(v) / 1000)}
                      />
                    </Col>
                  </Row>
                </Container>
              </Tab>

              <Tab
                eventKey="temp"
                title={<strong>{t("set_temperature_.label")}</strong>}
                tabClassName="px-3"
                disabled={fireSettingsDisabled}
              >
                <Row className="container-row pt-2" style={{ alignItems: "center" }}>
                  <Col xs={5} className="editfield-label ps-2">
                    <span>{t("maximum_member_temperature_.label")}</span>
                    <var dangerouslySetInnerHTML={{ __html: ", T<sub>max</sub>" }} />
                  </Col>
                  <Col xs={3}>
                    <SXEditField
                      id="sx-steel-fire-temperature"
                      type="number"
                      val={steelProtectionTemperature}
                      disabled={fireSettingsDisabled || activeSteelProtectionTabUi !== "temp"}
                      disableNegativeNumber
                      onUpdate={(v) => setSteelFireProtectionTemperature(Number(v))}
                    />
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Container>
        </Container>
      </Container>
    </>
  );
}
